import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

import { useTravelBookingByItem } from '../queries';
import { formatMoney } from '../money';
import {
  bookingStateColor,
  bookingStateLabelKey,
  isOnRequest,
} from '../bookingState';
import { BookingDetailModal } from './BookingDetailModal';
import { ActiveOperationAlert, ActiveOperationChip } from './ActiveOperationStatus';
import { useActiveOperationRecheck } from '../activeOperationRecheck';
import { SelectedBookingOptions } from './SelectedBookingOptions';

const STATE_COLOR: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  draft: 'default',
  quoted: 'info',
  held: 'info',
  confirmed: 'success',
  cancelled: 'error',
  failed: 'error',
};

/**
 * Vista del item de servicio (vertical de Rutero) dentro de la sale order:
 * resumen del booking vinculado + acceso al detalle operativo completo.
 */
export function RuteroSaleOrderItem({ itemId }: { itemId: string }) {
  const { t } = useTranslation();
  const { data: booking, isLoading, isError } = useTravelBookingByItem(itemId);
  const [detailOpen, setDetailOpen] = useState(false);
  useActiveOperationRecheck(booking);

  if (isLoading) return <CircularProgress color="inherit" />;
  if (isError || !booking) {
    return <Alert severity="warning">{t('travelServices.detail.itemNotLinked')}</Alert>;
  }

  const serviceName =
    booking.fareSnapshot?.name ?? booking.fareSnapshot?.title ?? '';

  return (
    <Box display="flex" flexDirection="column" gap={2} maxWidth={720}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            color={bookingStateColor(booking.state, booking.providerStatus, STATE_COLOR)}
            label={t(bookingStateLabelKey(booking.state, booking.providerStatus))}
          />
          {/* El matiz nativo del proveedor; cuando es on-request no se repite:
              el chip de estado ya lo dice traducido. */}
          {booking.providerStatus && !isOnRequest(booking.providerStatus) && (
            <Chip variant="outlined" size="small" label={booking.providerStatus} />
          )}
          <ActiveOperationChip booking={booking} />
        </Stack>
        <Typography variant="h6">
          {booking.finalAmountMinor != null && booking.currency
            ? formatMoney({
              amountMinor: booking.finalAmountMinor,
              currency: booking.currency,
            })
            : '—'}
        </Typography>
      </Box>

      {serviceName && <Typography variant="subtitle1">{serviceName}</Typography>}

      <Grid container spacing={1.5}>
        <Grid item xs={4}>
          <Typography variant="caption" color="text.secondary" display="block">
            {t('rutero.connectors.provider')}
          </Typography>
          <Typography variant="body2">{booking.provider ?? '—'}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="caption" color="text.secondary" display="block">
            {t('rutero.connectors.vertical')}
          </Typography>
          <Typography variant="body2">
            {t(`rutero.verticals.${booking.vertical}`)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="caption" color="text.secondary" display="block">
            Ref
          </Typography>
          <Typography variant="body2">{booking.providerReference ?? '—'}</Typography>
        </Grid>
      </Grid>

      <SelectedBookingOptions booking={booking} compact />

      {booking.state === 'held' && booking.expiresAt && (
        <Alert severity="warning">
          {t('travelServices.detail.holdExpires', {
            date: new Date(booking.expiresAt).toLocaleString(),
          })}
        </Alert>
      )}

      <ActiveOperationAlert booking={booking} />

      <Box>
        <Button variant="contained" onClick={() => setDetailOpen(true)}>
          {t('travelServices.detail.operate')}
        </Button>
      </Box>

      {detailOpen && (
        <BookingDetailModal booking={booking} onClose={() => setDetailOpen(false)} />
      )}
    </Box>
  );
}
